import { createContext, ReactNode, useContext, useEffect, useState, useCallback } from 'react';
import { WebSocketContext } from '../../websocket-context/websocket-provider';
import usePersistentState from '../../use-persistent-state';
import {
  IVolRangeRequestParams,
  IVolRangeResponse,
  IVolRangeItem,
  VolRangeData,
  LookbackEnum,
  TenorEnum,
  MetricEnum,
} from './types';

const KEY = 'VOLATILITY_RANGE';

interface VolatilityRangeProviderProps {
  children: ReactNode;
  providerKey?: string;
}

interface VolatilityRangeContextType {
  data: VolRangeData | null;
  isLoading: boolean;
  fetchVolRange: (params: IVolRangeRequestParams) => void;
  lastTimestamp: string;
  selectedLookback: LookbackEnum;
  setSelectedLookback: (lookback: LookbackEnum) => void;
  selectedTenor: TenorEnum;
  setSelectedTenor: (tenor: TenorEnum) => void;
  selectedMetric: MetricEnum;
  setSelectedMetric: (metric: MetricEnum) => void;
  isLive: boolean;
  setLastTimestamp: React.Dispatch<React.SetStateAction<string>>;
  resetToLive: () => void;
  setHistoricalDate: (timestamp: string) => void;
}

export const VolatilityRangeContext = createContext({} as VolatilityRangeContextType);

function isVolRangeResponse(obj: unknown): obj is IVolRangeResponse {
  if (typeof obj !== 'object' || obj === null) return false;
  const keys = ['current', 'low', 'p25', 'median', 'p75', 'high'];
  return keys.every(
    (key) =>
      key in obj &&
      typeof (obj as any)[key] === 'object' &&
      (obj as any)[key] !== null &&
      !Array.isArray((obj as any)[key]),
  );
}

export const VolatilityRangeProvider = ({
  children,
  providerKey,
}: VolatilityRangeProviderProps) => {
  const STATE_PATH = [KEY, `${providerKey || 'DEFAULT'}`];

  const { sendMessage, subscribeToChannel, isConnected } = useContext(WebSocketContext);

  const [data, setData] = useState<VolRangeData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastTimestamp, setLastTimestamp] = useState<string>(new Date().toISOString());

  const [selectedLookback, setSelectedLookback] = usePersistentState(
    STATE_PATH,
    'selectedLookback',
    LookbackEnum.M1,
  );
  const [selectedTenor, setSelectedTenor] = usePersistentState(
    STATE_PATH,
    'selectedTenor',
    TenorEnum.M1,
  );
  const [selectedMetric, setSelectedMetric] = usePersistentState(
    STATE_PATH,
    'selectedMetric',
    MetricEnum.ATMF,
  );
  const [isLive, setIsLive] = usePersistentState(STATE_PATH, 'isLive', true);

  const handleResponse = useCallback((response: any) => {
    const result = response?.result;

    if (isVolRangeResponse(result)) {
      const symbols = Object.keys(result.current || {});
      const items: IVolRangeItem[] = symbols.map((symbol) => ({
        symbol,
        current: result.current[symbol],
        low: result.low[symbol],
        p25: result.p25[symbol],
        median: result.median[symbol],
        p75: result.p75[symbol],
        high: result.high[symbol],
      }));

      setData(items);
      setLastTimestamp(new Date().toISOString());
    } else {
      setData(null);
    }
    setIsLoading(false);
  }, []);

  const fetchVolRange = useCallback(
    (params: IVolRangeRequestParams & { as_at_time?: string }) => {
      setIsLoading(true);
      const id = `VOL_RANGE_${providerKey || 'DEFAULT'}_${params.lookback}_${params.tenor}_${params.metric}`;
      const req = {
        jsonrpc: '2.0',
        id,
        method: 'ivol_range',
        params: {
          ...params,
          ...(params.as_at_time ? { as_at_time: params.as_at_time } : {}),
        },
      };
      sendMessage(JSON.stringify(req));
      subscribeToChannel(id, (res: any) => {
        handleResponse(res);
      });
    },
    [sendMessage, subscribeToChannel, providerKey, handleResponse],
  );

  const resetToLive = useCallback(() => {
    setIsLive(true);
    const now = new Date().toISOString();
    setLastTimestamp(now);
    fetchVolRange({
      lookback: selectedLookback,
      tenor: selectedTenor,
      metric: selectedMetric,
      format: 'json',
      as_at_time: now,
    });
  }, [selectedLookback, selectedTenor, selectedMetric, fetchVolRange]);

  const setHistoricalDate = useCallback(
    (timestamp: string) => {
      setIsLive(false);
      setLastTimestamp(timestamp);
      fetchVolRange({
        lookback: selectedLookback,
        tenor: selectedTenor,
        metric: selectedMetric,
        format: 'json',
        as_at_time: timestamp,
      });
    },
    [selectedLookback, selectedTenor, selectedMetric, fetchVolRange],
  );

  const updateParamsLoad = useCallback(() => {
    const params = {
      lookback: selectedLookback,
      tenor: selectedTenor,
      metric: selectedMetric,
      format: 'json',
      as_at_time: lastTimestamp,
    };
    fetchVolRange(params);
  }, [fetchVolRange, selectedLookback, selectedTenor, selectedMetric, lastTimestamp]);

  useEffect(() => {
    if (!isConnected) return;
    updateParamsLoad();
  }, [isConnected, isLive, selectedLookback, selectedTenor, selectedMetric]);

  return (
    <VolatilityRangeContext.Provider
      value={{
        data,
        isLoading,
        fetchVolRange,
        lastTimestamp,
        selectedLookback,
        setSelectedLookback,
        selectedTenor,
        setSelectedTenor,
        selectedMetric,
        setSelectedMetric,
        isLive,
        setLastTimestamp,
        resetToLive,
        setHistoricalDate,
      }}
    >
      {children}
    </VolatilityRangeContext.Provider>
  );
};
